import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import purchaseOrderService from '../../services/purchaseOrderService';
import { useTranslation } from 'react-i18next';
import { USER_ROLES } from '../../utils/constants';

const todayIso = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
};

const GenerateChallanForm = ({ isOpen, onClose, onSubmit, loading = false, adviceId, userRole = null }) => {
    const { t } = useTranslation();
    const currency = t('common.symbols.currency');

    const [adviceData, setAdviceData] = useState(null);
    const [fetching, setFetching] = useState(false);
    const [fetchError, setFetchError] = useState(null);

    const [expenses, setExpenses] = useState('');
    const [expenseRemark, setExpenseRemark] = useState('');
    const [discount, setDiscount] = useState('');
    const [discountRemark, setDiscountRemark] = useState('');
    const [priceOverrides, setPriceOverrides] = useState({}); // { item_id: 'price_string' }
    const [roundingOff, setRoundingOff] = useState('');
    const [remark, setRemark] = useState('');
    const [remarkError, setRemarkError] = useState(false);

    // Direct-flow fields
    const [vehicleNumber, setVehicleNumber] = useState('');
    const [driverContact, setDriverContact] = useState('');
    const [vehicleRemark, setVehicleRemark] = useState('');
    const [emptyTruckWeight, setEmptyTruckWeight] = useState('');
    const [loadedTruckWeight, setLoadedTruckWeight] = useState('');
    const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState('');
    const [supplierInvoiceDate, setSupplierInvoiceDate] = useState('');
    const [itemsUpdate, setItemsUpdate] = useState({}); // { item_id: { actual_quantity, actual_pieces, remark } }

    const canSeePoRemark = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN, USER_ROLES.SALES_STAFF, USER_ROLES.ACCOUNT_USER, USER_ROLES.OPERATIONS_MANAGER].includes(userRole);

    const lastFetchedId = useRef(null);

    useEffect(() => {
        if (isOpen && adviceId && lastFetchedId.current !== adviceId) {
            lastFetchedId.current = adviceId;
            setFetchError(null);
            setFetching(true);
            setAdviceData(null);
            setExpenses('');
            setExpenseRemark('');
            setDiscount('');
            setDiscountRemark('');
            setPriceOverrides({});
            setRoundingOff('');
            setRemark('');
            setRemarkError(false);
            setVehicleNumber('');
            setDriverContact('');
            setVehicleRemark('');
            setEmptyTruckWeight('');
            setLoadedTruckWeight('');
            setSupplierInvoiceNumber('');
            setSupplierInvoiceDate('');
            setItemsUpdate({});
            purchaseOrderService.getPurchaseAdviceById(adviceId)
                .then(async (res) => {
                    if (res.success) {
                        const advice = res.data;
                        if (advice.purchase_order_id && Array.isArray(advice.items)) {
                            const needsPricing = advice.items.some(it => {
                                const p = parseFloat(it.price);
                                return isNaN(p) || p <= 0;
                            });
                            
                            if (needsPricing) {
                                try {
                                    const poRes = await purchaseOrderService.getPurchaseOrderById(advice.purchase_order_id);
                                    if (poRes.success && Array.isArray(poRes.data?.items)) {
                                        const poItems = poRes.data.items;
                                        advice.items = advice.items.map(it => {
                                            const poItem = poItems.find(p => String(p.id) === String(it.purchase_order_item_id));
                                            if (poItem) {
                                                const advicePrice = parseFloat(it.price);
                                                const priceToUse = (!isNaN(advicePrice) && advicePrice > 0) ? it.price : poItem.price;

                                                const adviceGst = parseFloat(it.gst_percentage);
                                                const gstToUse = (!isNaN(adviceGst) && adviceGst > 0) ? it.gst_percentage : poItem.gst_percentage;
                                                
                                                return {
                                                    ...it,
                                                    price: priceToUse,
                                                    gst_percentage: gstToUse
                                                };
                                            }
                                            return it;
                                        });
                                    }
                                } catch (e) {
                                    console.error('Failed to fetch PO for price info:', e);
                                }
                            }
                        }
                        setAdviceData(advice);
                        setRemark(advice.remark || advice.po_remark || '');
                    }
                    else setFetchError(res.error || t('purchaseOrders.forms.generateChallanForm.messages.fetchFailed'));
                })
                .catch((err) => setFetchError(err.message || t('purchaseOrders.forms.generateChallanForm.messages.fetchFailed')))
                .finally(() => setFetching(false));
        }
        if (!isOpen) {
            lastFetchedId.current = null;
            setAdviceData(null);
        }
    }, [isOpen, adviceId, t]);

    const financialTotals = useMemo(() => {
        if (!adviceData || !adviceData.items) {
            return { 
                itemsBaseTotal: 0, 
                itemsGstTotal: 0, 
                itemsGrandTotal: 0, 
                expenseBase: 0,
                expenseGst: 0,
                discountBase: 0,
                discountGst: 0,
                grandTotal: 0, 
                itemsActualWeightTotal: 0,
                hasItemFinancials: false, 
                showSummary: false 
            };
        }

        let hasItemFinancials = false;
        
        // Use the first item's GST percentage for expenses and discounts, fallback to 18%
        const firstItemGstPercent = parseFloat(adviceData.items[0]?.gst_percentage) || 18.0;

        const itemsBaseTotal = adviceData.items.reduce((acc, it) => {
            const effectivePrice = parseFloat(priceOverrides[it.id] ?? it.price ?? 0);
            const hasPrice = !isNaN(effectivePrice) && effectivePrice > 0;
            const unit = (it.unit || '').toLowerCase();
            const isPieces = unit === 'pieces' || unit === 'pcs' || unit === 'piece';
            const rawQty = parseFloat(it.actual_quantity) || parseFloat(it.quantity) || 0;
            const qty = isPieces ? Math.round(rawQty) : rawQty;
            const amount = hasPrice ? effectivePrice * qty : 0;
            if (amount > 0 || hasPrice) hasItemFinancials = true;
            return acc + amount;
        }, 0);

        const itemsGstTotal = adviceData.items.reduce((acc, it) => {
            const effectivePrice = parseFloat(priceOverrides[it.id] ?? it.price ?? 0);
            const hasPrice = !isNaN(effectivePrice) && effectivePrice > 0;
            const unit = (it.unit || '').toLowerCase();
            const isPieces = unit === 'pieces' || unit === 'pcs' || unit === 'piece';
            const rawQty = parseFloat(it.actual_quantity) || parseFloat(it.quantity) || 0;
            const qty = isPieces ? Math.round(rawQty) : rawQty;
            const amount = hasPrice ? effectivePrice * qty : 0;
            const gstPercent = parseFloat(it.gst_percentage) || 0;
            return acc + (amount * gstPercent / 100);
        }, 0);
        
        const itemsGrandTotal = itemsBaseTotal + itemsGstTotal;
        const currentExpenseBase = parseFloat(expenses) || 0;
        const currentDiscountBase = parseFloat(discount) || 0;
        const currentExpenseGst = currentExpenseBase * (firstItemGstPercent / 100);
        const currentDiscountGst = currentDiscountBase * (firstItemGstPercent / 100);

        const itemsActualWeightTotal = adviceData.items.reduce((acc, it) => {
            const qty = parseFloat(it.actual_quantity) || parseFloat(it.quantity) || 0;
            return acc + qty;
        }, 0);

        const totalWithoutGst = itemsBaseTotal + currentExpenseBase - currentDiscountBase;
        const totalGst = itemsGstTotal + currentExpenseGst - currentDiscountGst;
        const grandTotal = totalWithoutGst + totalGst;

        const showSummary = hasItemFinancials || currentExpenseBase > 0 || currentDiscountBase > 0;

        // Check if all items with non-zero potential GST have the same rate
        const activeItemsGst = adviceData.items
            .map(it => parseFloat(it.gst_percentage) || 0)
            .filter(rate => rate > 0);
        
        const uniformItemGstRate = activeItemsGst.length > 0 && activeItemsGst.every(r => r === activeItemsGst[0])
            ? activeItemsGst[0]
            : null;

        return { 
            itemsBaseTotal, 
            itemsGstTotal, 
            itemsGrandTotal, 
            expenseBase: currentExpenseBase,
            expenseGst: currentExpenseGst,
            discountBase: currentDiscountBase,
            discountGst: currentDiscountGst,
            totalWithoutGst,
            totalGst,
            grandTotal, 
            itemsActualWeightTotal,
            hasItemFinancials, 
            showSummary,
            uniformItemGstRate,
            gstPercentage: firstItemGstPercent
        };
    }, [adviceData, expenses, discount, priceOverrides]);

    const handleSubmit = () => {
        if (!adviceId || !adviceData?.items) return;
        if (canSeePoRemark && !remark.trim()) {
            setRemarkError(true);
            return;
        }

        const isDirectFlow = !!adviceData.is_direct_flow;

        const item_price_overrides = adviceData.items.map(item => {
            const overridePrice = priceOverrides[item.id];
            const price = (overridePrice !== undefined && overridePrice !== '')
                ? parseFloat(overridePrice)
                : parseFloat(item.price) || 0;

            return {
                item_id: Number(item.id),
                price: price
            };
        });

        const autoRoundingOff = parseFloat((Math.round(financialTotals.grandTotal) - financialTotals.grandTotal).toFixed(2));
        const roundingOffValue = roundingOff !== '' ? parseFloat(roundingOff) || 0 : autoRoundingOff;

        const payload = {
            challan_date: todayIso(),
            expenses: financialTotals.expenseBase,
            expense_gst_amount: financialTotals.expenseGst,
            expense_remark: expenseRemark,
            discount: financialTotals.discountBase,
            discount_gst_amount: financialTotals.discountGst,
            discount_remark: discountRemark,
            item_price_overrides,
            rounding_off: roundingOffValue,
            remark: remark,
        };

        if (isDirectFlow) {
            payload.vehicle_number = vehicleNumber;
            payload.driver_contact = driverContact;
            payload.vehicle_remark = vehicleRemark;
            payload.empty_truck_weight = emptyTruckWeight !== '' ? parseFloat(emptyTruckWeight) : undefined;
            payload.loaded_truck_weight = loadedTruckWeight !== '' ? parseFloat(loadedTruckWeight) : undefined;
            payload.supplier_invoice_number = supplierInvoiceNumber || undefined;
            payload.supplier_invoice_date = supplierInvoiceDate || undefined;
            payload.items_update = adviceData.items.map(item => {
                const upd = itemsUpdate[item.id] || {};
                return {
                    id: Number(item.id),
                    actual_quantity: upd.actual_quantity !== undefined ? parseFloat(upd.actual_quantity) : undefined,
                    actual_pieces: upd.actual_pieces !== undefined ? parseFloat(upd.actual_pieces) : undefined,
                    remark: upd.remark,
                };
            });
        }

        onSubmit?.(payload);
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
        >
            <div
                className="w-full max-w-4xl bg-white rounded-2xl shadow-2xl max-h-[90vh] overflow-hidden flex flex-col"
                style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between p-6 border-b shrink-0"
                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                    <div>
                        <h2 className="text-2xl font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                            {t('purchaseOrders.forms.generateChallanForm.title')}
                        </h2>
                        <p className="mt-1 text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                            {t('purchaseOrders.forms.generateChallanForm.subtitle')}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg transition-colors hover:opacity-80"
                        style={{
                            color: UI_COLORS.TEXT.MUTED,
                            backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08),
                        }}
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto grow custom-scrollbar">
                    {fetching ? (
                        <div className="flex flex-col items-center justify-center py-10">
                            <div
                                className="h-10 w-10 animate-spin rounded-full border-4 border-b-transparent"
                                style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.5) }}
                            />
                            <p className="mt-4 text-sm font-semibold" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                {t('purchaseOrders.forms.generateChallanForm.messages.loading')}
                            </p>
                        </div>
                    ) : fetchError ? (
                        <div className="text-center py-10 text-red-500 font-medium">{fetchError}</div>
                    ) : adviceData ? (
                        <div className="space-y-8">

                            {/* Info Section */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                        {t('purchaseOrders.forms.generateChallanForm.fields.adviceNumber')}
                                    </label>
                                    <div
                                        className="w-full px-4 py-3 rounded-xl border-2"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                    >
                                        {adviceData.advice_number}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                        {t('purchaseOrders.forms.generateChallanForm.fields.supplier')}
                                    </label>
                                    <div
                                        className="w-full px-4 py-3 rounded-xl border-2 truncate"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                        title={adviceData.supplier_name}
                                    >
                                        {adviceData.supplier_name}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                        {t('purchaseOrders.forms.generateChallanForm.fields.poNumber')}
                                    </label>
                                    <div
                                        className="w-full px-4 py-3 rounded-xl border-2"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                    >
                                        {adviceData.po_number}
                                    </div>
                                </div>
                            </div>

                            {/* Direct flow: vehicle + truck weights + supplier invoice */}
                            {adviceData.is_direct_flow ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl border-2" style={{ backgroundColor: 'rgba(13, 148, 136, 0.04)', borderColor: 'rgba(13, 148, 136, 0.3)' }}>
                                    <div className="md:col-span-3">
                                        <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: '#0d9488' }}>{t('common.formFields.labels.vehicleAndDispatchDetails')}</p>
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.vehicleNumber')}</label>
                                        <input
                                            type="text"
                                            value={vehicleNumber}
                                            onChange={(e) => setVehicleNumber(e.target.value.slice(0, 20))}
                                            maxLength={20}
                                            className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors"
                                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                            placeholder={t('common.formFields.placeholders.vehicleNumberExample')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.driverContact')}</label>
                                        <input
                                            type="text"
                                            value={driverContact}
                                            onChange={(e) => setDriverContact(e.target.value.slice(0, 20))}
                                            maxLength={20}
                                            className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors"
                                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                            placeholder={t('common.formFields.placeholders.driverContactExample')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.vehicleRemark')}</label>
                                        <input
                                            type="text"
                                            value={vehicleRemark}
                                            onChange={(e) => setVehicleRemark(e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors"
                                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                            placeholder={t('common.formFields.placeholders.vehicleRemarkExample')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.emptyTruckWeightKg')}</label>
                                        <input
                                            type="number"
                                            value={emptyTruckWeight}
                                            onChange={(e) => setEmptyTruckWeight(e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            min="0"
                                            className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.loadedTruckWeightKg')}</label>
                                        <input
                                            type="number"
                                            value={loadedTruckWeight}
                                            onChange={(e) => setLoadedTruckWeight(e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                            min="0"
                                            className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                            placeholder="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.supplierInvoiceNo')}</label>
                                        <input
                                            type="text"
                                            value={supplierInvoiceNumber}
                                            onChange={(e) => setSupplierInvoiceNumber(e.target.value.slice(0, 100))}
                                            maxLength={100}
                                            className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors"
                                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                            placeholder={t('common.formFields.placeholders.supplierInvoiceNoExample')}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.supplierInvoiceDate')}</label>
                                        <input
                                            type="date"
                                            value={supplierInvoiceDate}
                                            onChange={(e) => setSupplierInvoiceDate(e.target.value)}
                                            className="w-full px-3 py-2 rounded-lg border-2 text-sm font-medium focus:outline-none focus:border-teal-400 transition-colors"
                                            style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                        />
                                    </div>
                                </div>
                            ) : adviceData.vehicle_number ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div>
                                        <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                            Vehicle Number
                                        </label>
                                        <div
                                            className="w-full px-4 py-3 rounded-xl border-2"
                                            style={{
                                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                                color: UI_COLORS.FORMS.INPUT.text,
                                            }}
                                        >
                                            {adviceData.vehicle_number}
                                        </div>
                                    </div>
                                </div>
                            ) : null}

                            {canSeePoRemark ? (
                                <div className="mt-4">
                                    <label className="block text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                        Order Remark <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        value={remark}
                                        onChange={(e) => { setRemark(e.target.value); if (e.target.value.trim()) setRemarkError(false); }}
                                        rows={3}
                                        className="w-full px-3 py-2 rounded-lg border-2 text-sm focus:outline-none transition-colors resize-none"
                                        style={{
                                            backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                            borderColor: remarkError ? '#ef4444' : UI_COLORS.FORMS.INPUT.border,
                                            color: UI_COLORS.FORMS.INPUT.text,
                                        }}
                                        placeholder={t('common.formFields.placeholders.orderRemark')}
                                    />
                                    {remarkError && (
                                        <p className="mt-1 text-xs text-red-500 font-medium">{t('common.formFields.errors.orderRemarkRequired')}</p>
                                    )}
                                </div>
                            ) : adviceData.remark ? (
                                <div className="mt-4">
                                    <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.formFields.labels.orderRemark')}</p>
                                    <div
                                        className="px-2 py-1.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200"
                                        style={{ whiteSpace: 'pre-wrap', width: '100%' }}
                                    >
                                        {adviceData.remark}
                                    </div>
                                </div>
                            ) : null}

                            {/* Items Section */}
                            <div>
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                    <span className="w-1.5 h-6 rounded-full" style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY }}></span>
                                    {t('purchaseOrders.forms.generateChallanForm.headings.items')}
                                </h3>
                                <div className="space-y-3">
                                    {adviceData.items.map((item, index) => {
                                        const price = parseFloat(item.price);
                                        const hasPrice = !isNaN(price) && price > 0;
                                        let amount = parseFloat(item.amount);
                                        if (isNaN(amount) || amount === 0) {
                                            if (hasPrice) {
                                                const unit = (item.unit || '').toLowerCase();
                                                const isPieces = unit === 'pieces' || unit === 'pcs' || unit === 'piece';
                                                const rawQty = parseFloat(item.actual_quantity) || parseFloat(item.quantity) || 0;
                                                const qty = isPieces ? Math.round(rawQty) : rawQty;
                                                amount = price * qty;
                                            } else {
                                                amount = 0;
                                            }
                                        }
                                        const hasAmount = amount > 0;

                                        return (
                                            <div
                                                key={item.id || index}
                                                className="p-4 rounded-xl border-2"
                                                style={{
                                                    backgroundColor: UI_COLORS.BACKGROUNDS.SECTION,
                                                    borderColor: UI_COLORS.BORDERS.LIGHT,
                                                }}
                                            >
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                    <div className={hasPrice || hasAmount ? "md:col-span-2" : "md:col-span-4"}>
                                                        <div className="text-sm font-bold text-gray-800">
                                                            {item.product_name}
                                                            {item.size_name && (
                                                                <span className="text-gray-500 font-medium ml-1">({item.size_name})</span>
                                                            )}
                                                        </div>
                                                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                                                            <div className="text-xs text-gray-500">
                                                                <span className="font-semibold">{t('purchaseOrders.forms.generateChallanForm.table.columns.requested')}:</span> {item.quantity} {item.unit}
                                                            </div>
                                                            {!adviceData.is_direct_flow && (
                                                                <div className="text-xs text-blue-600 font-bold">
                                                                    <span>{t('purchaseOrders.forms.generateChallanForm.table.columns.actualQty')}:</span> {Math.round(parseFloat(item.actual_quantity || 0))} {item.unit}
                                                                </div>
                                                            )}
                                                            <div className="text-xs text-gray-400">
                                                                <span>{t('purchaseOrders.forms.generateChallanForm.table.columns.poQty')}:</span> {item.po_item_quantity} {item.unit}
                                                            </div>
                                                        </div>
                                                        {adviceData.is_direct_flow && (
                                                            <div className="flex flex-wrap gap-3 mt-2">
                                                                <div>
                                                                    <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">{t('common.formFields.labels.actualQty')}</div>
                                                                    <input
                                                                        type="number"
                                                                        value={itemsUpdate[item.id]?.actual_quantity ?? ''}
                                                                        onChange={(e) => setItemsUpdate(prev => ({ ...prev, [item.id]: { ...prev[item.id], actual_quantity: e.target.value } }))}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        className="w-24 px-2 py-1.5 rounded-lg border-2 text-sm font-bold text-blue-600 focus:outline-none focus:border-teal-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                        style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                                                        placeholder={String(item.quantity || 0)}
                                                                        min="0"
                                                                    />
                                                                </div>
                                                                <div>
                                                                    <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">{t('common.formFields.labels.actualPieces')}</div>
                                                                    <input
                                                                        type="number"
                                                                        value={itemsUpdate[item.id]?.actual_pieces ?? ''}
                                                                        onChange={(e) => setItemsUpdate(prev => ({ ...prev, [item.id]: { ...prev[item.id], actual_pieces: e.target.value } }))}
                                                                        onWheel={(e) => e.target.blur()}
                                                                        className="w-24 px-2 py-1.5 rounded-lg border-2 text-sm font-bold text-gray-700 focus:outline-none focus:border-teal-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                        style={{ backgroundColor: UI_COLORS.FORMS.INPUT.background, borderColor: UI_COLORS.FORMS.INPUT.border }}
                                                                        placeholder="0"
                                                                        min="0"
                                                                    />
                                                                </div>
                                                            </div>
                                                        )}
                                                        {item.remark && (
                                                            <div 
                                                                className="text-xs mt-2 px-2 py-1.5 rounded-lg border border-yellow-200 font-medium" 
                                                                style={{ backgroundColor: '#fef9c3', color: '#854d0e', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', whiteSpace: 'pre-wrap' }}
                                                            >
                                                                <span className="font-bold opacity-70 uppercase text-[10px] mr-1">{t('purchaseOrders.forms.generateChallanForm.table.columns.remark')}:</span> {item.remark}
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Always show editable price input */}
                                                    <div className="flex flex-col justify-center">
                                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{t('purchaseOrders.forms.generateChallanForm.table.columns.rate')}</div>
                                                        <div className="relative">
                                                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">{currency}</span>
                                                            <input
                                                                type="number"
                                                                value={priceOverrides[item.id] ?? (parseFloat(item.price) > 0 ? item.price : '')}
                                                                onChange={(e) => setPriceOverrides(prev => ({ ...prev, [item.id]: e.target.value }))}
                                                                onWheel={(e) => e.target.blur()}
                                                                className="w-full pl-5 pr-2 py-1.5 rounded-lg border-2 text-sm font-bold text-gray-800 focus:outline-none focus:border-blue-400 transition-colors [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                                style={{
                                                                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                                    borderColor: priceOverrides[item.id] !== undefined ? '#3b82f6' : UI_COLORS.FORMS.INPUT.border,
                                                                }}
                                                                placeholder="0.00"
                                                                step="0.01"
                                                                min="0"
                                                            />
                                                        </div>
                                                        {parseFloat(item.gst_percentage) > 0 && (
                                                            <div className="text-[10px] text-gray-400 mt-1 font-medium">
                                                                GST: {parseFloat(item.gst_percentage)}%
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Live-computed amount */}
                                                    <div className="flex flex-col justify-center items-end">
                                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{t('purchaseOrders.forms.generateChallanForm.table.columns.total')}</div>
                                                        <div className="text-base font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                                            {(() => {
                                                                const ep = parseFloat(priceOverrides[item.id] ?? item.price ?? 0);
                                                                const unit = (item.unit || '').toLowerCase();
                                                                const isPieces = unit === 'pieces' || unit === 'pcs' || unit === 'piece';
                                                                const rawQty = parseFloat(item.actual_quantity) || parseFloat(item.quantity) || 0;
                                                                const qty = isPieces ? Math.round(rawQty) : rawQty;
                                                                return ep > 0 ? `${currency} ${(ep * qty).toFixed(2)}` : '—';
                                                            })()}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Financial Adjustments Section */}
                            <div>
                                <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                    <span className="w-1.5 h-6 rounded-full" style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY }}></span>
                                    {t('purchaseOrders.forms.generateChallanForm.headings.adjustments')}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6 rounded-2xl border-2 bg-gray-50 border-gray-100">
                                    <div className="space-y-3">
                                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                            {t('purchaseOrders.forms.generateChallanForm.fields.expenses')}
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-3 text-gray-400">{currency}</span>
                                            <input
                                                type="number"
                                                value={expenses}
                                                onChange={(e) => setExpenses(e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full pl-8 pr-4 py-3 rounded-xl border-2 focus:outline-none focus:border-blue-500 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                style={{
                                                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                    borderColor: UI_COLORS.FORMS.INPUT.border,
                                                }}
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            value={expenseRemark}
                                            onChange={(e) => setExpenseRemark(e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:border-blue-500 transition-all text-sm"
                                            style={{
                                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                            }}
                                            placeholder={t('purchaseOrders.forms.generateChallanForm.fields.expenseRemark')}
                                        />
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-semibold" style={{ color: UI_COLORS.FORMS.LABEL }}>
                                            {t('purchaseOrders.forms.generateChallanForm.fields.discount')}
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-4 top-3 text-gray-400">{currency}</span>
                                            <input
                                                type="number"
                                                value={discount}
                                                onChange={(e) => setDiscount(e.target.value)}
                                                onWheel={(e) => e.target.blur()}
                                                className="w-full pl-8 pr-4 py-3 rounded-xl border-2 focus:outline-none focus:border-green-500 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                style={{
                                                    backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                    borderColor: UI_COLORS.FORMS.INPUT.border,
                                                }}
                                                placeholder="0.00"
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            value={discountRemark}
                                            onChange={(e) => setDiscountRemark(e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border-2 focus:outline-none focus:border-green-500 transition-all text-sm"
                                            style={{
                                                backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                borderColor: UI_COLORS.FORMS.INPUT.border,
                                            }}
                                            placeholder={t('purchaseOrders.forms.generateChallanForm.fields.discountRemark')}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Financial Summary */}
                            {financialTotals.showSummary && (
                                <div className="flex justify-end border-t pt-8" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                                    <div className="w-full md:w-1/2 bg-blue-50 p-6 rounded-2xl border border-blue-100 space-y-3">
                                        
                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>{t('common.formFields.labels.subtotalItems')}</span>
                                            <span className="font-medium">{currency} {financialTotals.itemsBaseTotal.toFixed(2)}</span>
                                        </div>

                                        {financialTotals.expenseBase > 0 && (
                                            <div className="flex justify-between text-sm text-orange-600">
                                                <span>{t('common.formFields.labels.expenses')}</span>
                                                <span className="font-medium">{currency} {financialTotals.expenseBase.toFixed(2)}</span>
                                            </div>
                                        )}

                                        {financialTotals.discountBase > 0 && (
                                            <div className="flex justify-between text-sm text-green-600">
                                                <span>{t('common.formFields.labels.discount')}</span>
                                                <span className="font-medium">- {currency} {financialTotals.discountBase.toFixed(2)}</span>
                                            </div>
                                        )}
                                        
                                        <div className="pt-2 border-t border-blue-200 flex justify-between text-sm font-bold text-blue-800">
                                            <span>{t('common.formFields.labels.totalWithoutGst')}</span>
                                            <span>{currency} {financialTotals.totalWithoutGst.toFixed(2)}</span>
                                        </div>

                                        <div className="flex justify-between text-sm text-gray-600">
                                            <span>{t('common.formFields.labels.gstAtPercent', { pct: financialTotals.gstPercentage.toFixed(2) })}</span>
                                            <span className="font-medium">{currency} {financialTotals.totalGst.toFixed(2)}</span>
                                        </div>

                                        <div className="pt-2 border-t border-blue-200 flex justify-between text-sm font-bold text-blue-800">
                                            <span>{t('common.formFields.labels.totalIncGst')}</span>
                                            <span>{currency} {financialTotals.grandTotal.toFixed(2)}</span>
                                        </div>

                                        {/* Rounding Off */}
                                        <div className="flex justify-between items-center text-sm text-gray-600">
                                            <span className="font-medium">{t('common.formFields.labels.roundingOff')}</span>
                                            <div className="relative w-32">
                                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">{currency}</span>
                                                <input
                                                    type="number"
                                                    value={roundingOff !== '' ? roundingOff : parseFloat((Math.round(financialTotals.grandTotal) - financialTotals.grandTotal).toFixed(2))}
                                                    onChange={(e) => setRoundingOff(e.target.value)}
                                                    onWheel={(e) => e.target.blur()}
                                                    className="w-full pl-5 pr-2 py-1.5 rounded-lg border-2 text-sm font-bold text-gray-800 focus:outline-none focus:border-blue-400 transition-colors text-right [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                                    style={{
                                                        backgroundColor: UI_COLORS.FORMS.INPUT.background,
                                                        borderColor: roundingOff !== '' ? '#3b82f6' : UI_COLORS.FORMS.INPUT.border,
                                                    }}
                                                    step="0.01"
                                                />
                                            </div>
                                        </div>

                                        {/* Grand Total */}
                                        <div class="pt-3 border-t-2 border-blue-400 flex justify-between items-end">
                                            <div>
                                                <p className="text-xs font-bold text-gray-400 uppercase tracking-tighter leading-none mb-1">{t('common.formFields.labels.totalWeight')}</p>
                                                <p className="text-lg font-bold text-gray-700">{Math.round(financialTotals.itemsActualWeightTotal)} KG</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest leading-none mb-1">{t('purchaseOrders.forms.generateChallanForm.summaryLabels.grandTotal')}</p>
                                                <h4 className="text-3xl font-black text-blue-900 tracking-tighter">{currency} {Math.round(financialTotals.grandTotal + (roundingOff !== '' ? parseFloat(roundingOff) || 0 : parseFloat((Math.round(financialTotals.grandTotal) - financialTotals.grandTotal).toFixed(2))))}</h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                        </div>
                    ) : null}
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-end gap-4 p-6 border-t shrink-0"
                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}
                >
                    <button
                        onClick={onClose}
                        className="px-6 py-3 rounded-xl font-medium transition-colors border"
                        disabled={loading}
                        style={{
                            backgroundColor: UI_COLORS.BUTTONS.SECONDARY.background,
                            color: UI_COLORS.BUTTONS.SECONDARY.text,
                            borderColor: UI_COLORS.BORDERS.MEDIUM,
                        }}
                    >
                        {t('purchaseOrders.forms.generateChallanForm.buttons.cancel')}
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={loading || !adviceData}
                        className="px-6 py-3 rounded-xl font-bold shadow-lg transition-all hover:shadow-xl disabled:opacity-70 flex items-center gap-2"
                        style={{
                            backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                            color: UI_COLORS.BUTTONS.PRIMARY.text,
                        }}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin />
                                <span>{t('purchaseOrders.forms.generateChallanForm.buttons.generating')}</span>
                            </>
                        ) : (
                            <>
                                <FontAwesomeIcon icon={faFileInvoiceDollar} />
                                <span>{t('purchaseOrders.forms.generateChallanForm.buttons.generate')}</span>
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GenerateChallanForm;
